import { apiService } from './apiService';
import type { SissiBand, SissiRolling7 } from './sissiScoreService';
import { API_BASE_URL } from '../config/env';

export interface Patient {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone_number: string;
  username: string;
  date_of_birth: string;
  gender: string;
  address: string;
  avatarUrl: string | null;
  rmrKcalDay: number;
  weight: number;
  // Altura del perfil, en cm: no es una medida corporal (no tiene histórico),
  // solo se combina con el último peso para el IMC.
  heightCm?: number | null;
  termsAndConditionsAccepted: string | null;
  lastActivityAt: string | null;
  app_version: string | null;
  // Sistema operativo del móvil del paciente. Sólo lectura: la app lo
  // reescribe en cada actualización de perfil, y el backend ignora ambos
  // campos en PUT/PATCH desde la clínica. El backend los normaliza a
  // minúsculas, así que basta comparar con 'android' / 'ios'.
  app_os: 'android' | 'ios' | null;
  app_os_version: string | null;
  fcm_token: string | null;
  weeklyDigestEnabled?: boolean;
  insightsEnabled?: boolean;
  serviceStatus?: ServiceStatus;
  /** Pending insight findings. Counted by the endpoint, not by the client. */
  openInsights?: number;
  /** Why this patient needs a decision, or null when they do not. Computed by
   *  the endpoint with the same expression that orders the list, so no screen
   *  has to work it out again and disagree. */
  attentionReason?: 'never' | 'offPlan' | 'inactive' | 'watch' | null;
  /** Scores of the last 7 days with data, oldest first. A day the patient did
   *  not take part in is absent rather than zero, same as the rolling mean. */
  sissiSparkline?: { date: string; score: number }[];
  assignedProfessional?: AssignedProfessional | null;
  clinic?: Clinic | null;
  // Último día CON score del paciente, que no tiene por qué ser hoy. null sólo
  // si nunca ha tenido ninguno.
  sissiScore?: PatientSissiScore | null;
}

// Resumen que el listado anida por paciente (una subconsulta, sin N+1). La
// banda la calcula el servidor: el cliente no vuelve a aplicar umbrales.
//
// El servidor devuelve la fila más reciente CON score, no la más reciente a
// secas: el cron escribe la fila de hoy para todo el mundo a las 02:30, sin
// score hasta que el paciente registra algo, así que ordenar sólo por fecha
// dejaría el listado entero en blanco cada mañana. Por eso `date` puede ser de
// hace días y el badge lo enseña siempre al pasar el ratón.
export interface PatientSissiScore {
  score: number | null; // 0–100 — el día suelto
  band: SissiBand | null; // null cuando no hay score
  // Lo que pinta el anillo. El valor diario no sirve para escanear una cohorte:
  // cae a 0 el día que el paciente no registra, así que la columna parpadearía.
  // Llega con una consulta extra por página, no por paciente.
  rolling7: SissiRolling7 | null;
  date: string; // YYYY-MM-DD — el día del score, no necesariamente hoy
  dataConfidence: number | null; // 0–1
  isProvisional: boolean;
  isCalibrating: boolean;
}

export interface AssignedProfessional {
  id: number;
  name: string;
  email: string;
  role: string;
}

export interface Clinic {
  id: number;
  name: string;
}

export interface ServiceStatus {
  pushNotifications: boolean;
  agentEnabled: boolean;
  agentProactiveEnabled: boolean;
  sleepEnabled: boolean;
  insightsEnabled: boolean;
}

export interface CreatePatientData {
  email: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  rmrKcalDay: number;
  weight: number;
  phoneNumber?: string;
  password?: string;
  welcomeEmailType?: 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword';
}

export interface UpdatePatientData {
  email: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  rmrKcalDay: number;
  weight: number;
  phone_number: string;
  address: string;
  weeklyDigestEnabled: boolean;
  insightsEnabled: boolean;
}

export interface PaginatedResponse {
  actualPage: number;
  count: number;
  perpage: number;
  results: Patient[];
  totalPages: number;
}

export type PatientActivityStatus = 'active' | 'inactive' | 'never';

export const getPatientActivityStatus = (patient: Patient): PatientActivityStatus => {
  if (!patient.lastActivityAt) return 'never';
  const lastActivity = new Date(patient.lastActivityAt);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return lastActivity < oneWeekAgo ? 'inactive' : 'active';
};

export type PatientOrdering =
  | 'attention'
  | 'recent'
  | 'name'
  | '-name'
  | 'activity'
  | '-activity'
  | 'sissi'
  | '-sissi'
  | 'insights';

export interface PatientListQuery {
  page?: number;
  search?: string;
  ordering?: PatientOrdering;
  /** Sissi bands to keep. Empty means every band. */
  bands?: SissiBand[];
  /** Activity buckets to keep. Empty means every bucket. */
  activity?: PatientActivityStatus[];
  /** Only patients with at least one pending insight. */
  hasInsights?: boolean;
  /** Only patients the endpoint flagged as needing attention. */
  needsAttention?: boolean;
  perPage?: number;
}

export const patientsService = {
  getPatients: async (query: PatientListQuery = {}): Promise<PaginatedResponse> => {
    const params = new URLSearchParams({
      is_active: 'true',
      page: String(query.page ?? 1),
    });
    if (query.search?.trim()) params.set('search', query.search.trim());
    if (query.ordering) params.set('ordering', query.ordering);
    if (query.bands?.length) params.set('band', query.bands.join(','));
    if (query.activity?.length) params.set('activity', query.activity.join(','));
    if (query.hasInsights) params.set('has_insights', '1');
    if (query.needsAttention) params.set('needs_attention', '1');
    if (query.perPage) params.set('per_page', String(query.perPage));

    try {
      return await apiService.get(`${API_BASE_URL}/clinic/patient?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  },

  getPatientById: async (id: string | number): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching patient with id ${id}:`, error);
      throw error;
    }
  },

  createPatient: async (data: CreatePatientData): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient`;
    try {
      const payload: any = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        dateOfBirth: data.dateOfBirth,
        rmrKcalDay: data.rmrKcalDay,
        weight: data.weight,
      };
      
      if (data.phoneNumber) {
        payload.phone_number = data.phoneNumber;
      }
      
      if (data.password) {
        payload.password = data.password;
      }
      
      if (data.welcomeEmailType) {
        payload.welcomeEmailType = data.welcomeEmailType;
      }
      
      const response = await apiService.post(url, payload);
      return response;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  },

  updatePatient: async (id: string | number, data: Partial<UpdatePatientData>): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.put(url, data);
      return response;
    } catch (error) {
      console.error(`Error updating patient with id ${id}:`, error);
      throw error;
    }
  },

  // Partial update via PATCH — used for standalone field toggles (e.g.
  // insightsEnabled, weeklyDigestEnabled) without sending the full patient form.
  patchPatient: async (id: string | number, data: Partial<UpdatePatientData>): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.patch(url, data);
      return response;
    } catch (error) {
      console.error(`Error patching patient with id ${id}:`, error);
      throw error;
    }
  },

  updatePatientPassword: async (id: string | number, password: string): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.put(url, { password });
      return response;
    } catch (error) {
      console.error(`Error updating password for patient with id ${id}:`, error);
      throw error;
    }
  },

  sendWelcomeEmail: async (userId: number, welcomeEmailType: 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword' | 'accessReminder'): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/welcome-email/`;
    try {
      await apiService.post(url, { welcomeEmailType });
    } catch (error) {
      console.error(`Error sending welcome email to user ${userId}:`, error);
      throw error;
    }
  },

  /** The same endpoint the other way round. Deactivation is reversible, which
   *  is what lets the confirmation promise it and the toast offer an undo. */
  reactivatePatient: async (userId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/activation/`;
    try {
      await apiService.patch(url, { is_active: true });
    } catch (error) {
      console.error('Error reactivating patient:', error);
      throw error;
    }
  },

  deactivatePatient: async (userId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/activation/`;
    try {
      await apiService.patch(url, { is_active: false });
    } catch (error) {
      console.error(`Error deactivating patient with userId ${userId}:`, error);
      throw error;
    }
  },
}; 